'use client'

// ActionsHud — the town offering you things to do.
//
// Walk up to a place with actions and an "Actions" button appears; open it
// and the place deals you a hand: some ordinary, some fun (purple dot),
// some mischief (red dot), a couple locked behind knowing him better.
// Doing one writes a trace into the sim and he finds it later, at that
// place, in his own time.
//
// The panel sits exactly where the journal sits and borrows its bones, in
// dark grey with a black border: same furniture, different room. Cream
// panels tell you things; this one is for doing things, and the colour is
// how you know before you read a word.
//
// Scarcity is deliberate everywhere here. A daily allowance (rank-sized,
// reset at midnight UTC, shown with a countdown) makes a deed a decision
// rather than a spree, keeps busy places from flooding his diary, and is
// the reason to come back tomorrow. The per-open reshuffle is what makes
// a deep pool feel bottomless.
//
// His post is its own popup (PinoMail, below): an email client the size of
// a postcard, because an inbox pretending to be a list row is neither.

import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import useGameStore from '../../stores/gameStore'
import { authFetch, useAccount } from '../auth/useUser'
import { POINTS_OF_INTEREST } from '../../lib/world/sceneConfig'
import { ACTION_PLACES } from '../../lib/world/placeActions'
import { SITE_PLACES, SITE_PLACE_NAMES, sitesAt } from '../../lib/world/sitePlaces'
import { agoShort } from '../../lib/ui/time'
import { ink } from '../../lib/ui/theme'

const NEAR = 10
const POLL_MS = 450


// Honest label while the system beds in: users are testing it with us.
// Module scope, not inside the component: a component defined during
// render is a new type every frame and React remounts it.
const BetaPill = () => (
  <span style={{
    background: 'transparent', color: ink.dim, borderRadius: 999,
    border: `1.5px solid ${ink.dim}`, padding: '2px 8px',
    fontSize: 10, fontWeight: 800, letterSpacing: '0.06em',
    lineHeight: 1.3, display: 'inline-flex', alignItems: 'center',
  }}>
    BETA
  </span>
)

// Doorsteps: places with things to do, and places with a page of his.
// The printers, the studio and the walls have no place actions, so this
// used to walk straight past them.
const ACTION_POIS = POINTS_OF_INTEREST.filter((p) => ACTION_PLACES.includes(p.name) || SITE_PLACE_NAMES.includes(p.name))
const SITES_MS = 5 * 60_000

function TickCircle() {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" aria-hidden="true" style={{ flexShrink: 0 }}>
      <circle cx="11" cy="11" r="10" fill="none" stroke={ink.good} strokeWidth="2" />
      <path d="M6.5 11.5l3 3 6-7" fill="none" stroke={ink.good} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function untilReset(resetsAt) {
  const ms = Math.max(0, resetsAt - Date.now())
  const h = Math.floor(ms / 3600e3)
  const m = Math.ceil((ms % 3600e3) / 60e3)
  return h > 0 ? `${h}h ${m}m` : `${m}m`
}

export function ActionsHud() {
  const [nearPlace, setNearPlace] = useState(null)
  const [open, setOpen] = useState(false)
  const [mailOpen, setMailOpen] = useState(false)
  const [hand, setHand] = useState(null)
  const [view, setView] = useState('do')          // 'do' | 'yours'
  const [mine, setMine] = useState(null)
  const [unread, setUnread] = useState(0)
  const [busy, setBusy] = useState('')
  const [done, setDone] = useState({})
  const [budget, setBudget] = useState(null)
  const [, forceTick] = useState(0)
  const visit = useRef(0)
  const rootRef = useRef(null)
  const mailRef = useRef(null)
  const { signedIn } = useAccount()

  // Where the walker is. Poll rather than subscribe: the answer only
  // matters at footstep granularity.
  useEffect(() => {
    const t = setInterval(() => {
      const get = useGameStore.getState().playerPosGetter
      if (!get) { setNearPlace(null); return }
      const pos = get()
      if (!pos) { setNearPlace(null); return }
      let best = null
      let bestD = NEAR * NEAR
      for (const p of ACTION_POIS) {
        const dx = pos.x - p.position[0]
        const dz = pos.z - p.position[2]
        const d = dx * dx + dz * dz
        if (d < bestD) { bestD = d; best = p.name }
      }
      setNearPlace(best)
    }, POLL_MS)
    return () => clearInterval(t)
  }, [])

  // Which of his pages exist right now, refreshed slowly: a site he
  // archives loses its door on the next read, and nothing else about
  // the list moves faster than his sittings do.
  const [sites, setSites] = useState([])
  useEffect(() => {
    let dead = false
    const read = () => fetch('/api/sites', { cache: 'no-store' })
      .then((r) => r.json())
      .then((d) => { if (!dead && Array.isArray(d?.sites)) setSites(d.sites) })
      .catch(() => { /* the doors stay as they were */ })
    read()
    const t = setInterval(read, SITES_MS)
    return () => { dead = true; clearInterval(t) }
  }, [])

  // The countdown re-renders itself once a minute while anything is open.
  useEffect(() => {
    if (!open && !mailOpen) return
    const t = setInterval(() => forceTick((n) => n + 1), 30_000)
    return () => clearInterval(t)
  }, [open, mailOpen])

  // Walking away closes everything: these belong to the doorstep.
  useEffect(() => {
    if (!nearPlace) { setOpen(false); setMailOpen(false); setHand(null) }
  }, [nearPlace])

  useEffect(() => {
    if (nearPlace !== 'surfInternet' || !signedIn) { setUnread(0); return }
    let dead = false
    authFetch('/api/mail?peek=1', { cache: 'no-store' })
      .then((r) => r.json())
      .then((d) => { if (!dead) setUnread(d.unread || 0) })
      .catch(() => {})
    return () => { dead = true }
  }, [nearPlace, signedIn])

  const hasActions = !!nearPlace && ACTION_PLACES.includes(nearPlace)
  const placeSites = useMemo(() => sitesAt(nearPlace, sites), [nearPlace, sites])

  const openPopup = useCallback(async () => {
    if (!nearPlace) return
    visit.current += 1
    setOpen(true)
    setMailOpen(false)
    setHand(null)
    setDone({})
    setView('do')
    // A doorstep with only a page of his has no hand to deal.
    if (!ACTION_PLACES.includes(nearPlace)) {
      setHand({ title: SITE_PLACES[nearPlace]?.title || nearPlace, siteOnly: true })
      return
    }
    try {
      const r = await authFetch(`/api/actions?place=${nearPlace}&visit=${visit.current}`, { cache: 'no-store' })
      const d = await r.json()
      setHand(d)
      if (d.budget) setBudget(d.budget)
    } catch {
      setHand({ error: 'the town is not answering' })
    }
  }, [nearPlace])

  // Two windows, one desk. PMAIL floats beside the actions panel rather
  // than replacing it, and the closing rules are a desk's rules: shutting
  // the mail window leaves your hand of actions where it was, shutting the
  // actions panel (or walking off) clears the whole desk. Escape peels the
  // top window first. The outside-click for the actions panel treats the
  // mail window as inside, or clicking your inbox would sweep the desk.
  useEffect(() => {
    if (!open) return
    const onKey = (e) => {
      if (e.key !== 'Escape') return
      if (mailOpen) { setMailOpen(false); return }
      setOpen(false)
    }
    const onDown = (e) => {
      if (rootRef.current && rootRef.current.contains(e.target)) return
      if (mailRef.current && mailRef.current.contains(e.target)) return
      setOpen(false)
      setMailOpen(false)
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener('pointerdown', onDown)
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('pointerdown', onDown) }
  }, [open, mailOpen])

  const openYours = useCallback(async () => {
    setView('yours')
    setMine(null)
    try {
      const r = await authFetch('/api/actions?mine=1', { cache: 'no-store' })
      const d = await r.json()
      setMine(r.ok ? (d.mine || []) : { error: d.error || 'could not fetch' })
    } catch {
      setMine({ error: 'the town is not answering' })
    }
  }, [])

  const doAction = async (a) => {
    if (a.locked || done[a.id] || busy) return
    setBusy(a.id)
    try {
      const r = await authFetch('/api/actions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ place: nearPlace, id: a.id }),
      })
      const d = await r.json()
      if (d.budget) setBudget(d.budget)
      setDone((m) => ({ ...m, [a.id]: r.ok ? 'ok' : (d.error || 'no') }))
    } catch {
      setDone((m) => ({ ...m, [a.id]: 'the town is not answering' }))
    }
    setBusy('')
  }

  if (!nearPlace) return null
  // A page he has archived takes its door with it: nothing to offer here.
  if (!hasActions && placeSites.length === 0) return null

  return (
    <>
      {!open && !mailOpen && (
        <button
          type="button"
          onClick={openPopup}
          className="fixed left-1/2 -translate-x-1/2 z-40 pointer-events-auto"
          style={{
            bottom: 84,
            background: ink.bg,
            border: `2.5px solid ${ink.border}`,
            borderRadius: 999,
            color: ink.text,
            padding: '9px 20px',
            fontSize: 13.5,
            fontWeight: 800,
            boxShadow: '0 3px 0 rgba(0, 0, 0, 0.5)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {hasActions ? 'Actions' : 'His website'}
          {hasActions && <BetaPill />}
          {unread > 0 && (
            <span style={{
              background: ink.line, color: ink.text, borderRadius: 999,
              padding: '3px 10px', fontSize: 11.5, lineHeight: 1.2,
              display: 'inline-flex', alignItems: 'center',
            }}>
              You&apos;ve got mail
            </span>
          )}
        </button>
      )}

      {open && (
        <div
          ref={rootRef}
          className={[
            'fixed z-50 pointer-events-auto',
            // The journal's exact frame: full-screen on a phone, the left
            // panel on desktop, stopping short of the minimap disc.
            'top-0 left-0 right-0 bottom-0',
            'md:top-4 md:left-6 md:bottom-[10.5rem] md:right-auto md:w-[370px]',
          ].join(' ')}
        >
          <div
            className="relative h-full flex flex-col overflow-hidden"
            style={{
              background: ink.bg,
              border: `2.5px solid ${ink.border}`,
              borderRadius: 22,
              boxShadow: '0 6px 0 rgba(0, 0, 0, 0.45)',
            }}
          >
            <div style={{ position: 'absolute', inset: 4, border: `2px dashed ${ink.stitch}`, borderRadius: 18, pointerEvents: 'none', zIndex: 2 }} />

            <div style={{ padding: '16px 18px 8px' }}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center" style={{ gap: 8 }}>
                    <div style={{ color: ink.text, fontSize: 17, fontWeight: 800 }}>{hand?.title || ''}</div>
                    {hasActions && <BetaPill />}
                  </div>
                  <div style={{ color: ink.dim, fontSize: 12.5, lineHeight: 1.45, marginTop: 2 }}>
                    {hasActions
                      ? <>make small changes or actions in Pino&apos;s world for him to discover</>
                      : <>he keeps a page about this place. he made it himself.</>}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => { setOpen(false); setMailOpen(false) }}
                  aria-label="close"
                  style={{
                    width: 30, height: 30, borderRadius: 999, background: ink.bgDeep, flexShrink: 0,
                    border: `2px solid ${ink.border}`, color: ink.text, fontWeight: 800, cursor: 'pointer',
                  }}
                >
                  x
                </button>
              </div>

              {hasActions && (
              <div className="flex items-center justify-between" style={{ marginTop: 9 }}>
                {/* The key. Two dots is a code; a code needs a legend. */}
                <div className="flex items-center" style={{ gap: 14 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: ink.dim, fontSize: 11.5 }}>
                    <span style={{ width: 7, height: 7, borderRadius: 99, background: ink.soft }} /> fun
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: ink.dim, fontSize: 11.5 }}>
                    <span style={{ width: 7, height: 7, borderRadius: 99, background: ink.danger }} /> mischief
                  </span>
                </div>
                <div className="flex items-center" style={{ gap: 6 }}>
                  {['do', 'yours'].map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => (v === 'yours' ? openYours() : setView('do'))}
                      style={{
                        background: view === v ? ink.line : ink.bgDeep,
                        border: `2px solid ${ink.border}`,
                        borderRadius: 999, color: ink.text, fontSize: 11.5, fontWeight: 800,
                        padding: '3px 11px', cursor: 'pointer',
                      }}
                    >
                      {v === 'do' ? 'do' : 'your actions'}
                    </button>
                  ))}
                </div>
              </div>
              )}

              {budget && hasActions && (
                <div
                  style={{
                    marginTop: 10, background: ink.bgDeep, border: `2px solid ${ink.border}`,
                    borderRadius: 10, padding: '7px 11px', display: 'flex',
                    justifyContent: 'space-between', alignItems: 'center',
                  }}
                >
                  <span style={{ color: budget.left > 0 ? ink.text : ink.danger, fontSize: 12.5, fontWeight: 800 }}>
                    {budget.left > 0 ? `${budget.left} of ${budget.cap} actions left today` : 'no actions left today'}
                  </span>
                  <span style={{ color: ink.dim, fontSize: 11.5 }}>resets in {untilReset(budget.resetsAt)}</span>
                </div>
              )}
            </div>

            <div className="kawaii-scroll flex-1 min-h-0" style={{ overflowY: 'auto', padding: '4px 14px 16px' }}>
              {view === 'do' && placeSites.length > 0 && <SiteRows sites={placeSites} />}
              {view === 'do' && hasActions && (
                <ActionList
                  hand={hand}
                  done={done}
                  busy={busy}
                  signedIn={signedIn}
                  onDo={doAction}
                  onMail={() => setMailOpen(true)}
                  unread={unread}
                  budget={budget}
                />
              )}
              {view === 'yours' && (
                <YoursList
                  mine={mine}
                  signedIn={signedIn}
                  onSeeDiary={(m) => {
                    // Hand the diary a target and get out of its way: same
                    // frame, so this panel closing IS the journal opening.
                    // The about tab, not the diary window: the window rolls
                    // and an old find fell out of it, which read as the
                    // entry not existing. The about tab reads the whole
                    // file, so the line is always there to land on.
                    const g = useGameStore.getState()
                    g.setJournalFocus({ ref: m.id, tab: 'about' })
                    g.setJournalOpen(true)
                    setOpen(false)
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}

      <PinoMailPopup
        open={mailOpen}
        onClose={() => setMailOpen(false)}
        onEmptied={() => setUnread(0)}
        frameRef={mailRef}
      />
    </>
  )
}


// His pages, offered at the place they are about. Opens in a new tab:
// the town stays where you left it. The kind word says what you will
// find, because "Bird Graffiti Guide" says it and "print-workshop" does
// not.
const KIND_WORDS = { graffiti: 'the walls, online', prints: 'the prints, in 3D', cats: 'the cats', sketches: 'his sketches', page: 'a page of his' }
function SiteRows({ sites }) {
  return (
    <div style={{ margin: '8px 0 12px' }}>
      <div style={{ color: ink.dim, fontSize: 11.5, fontWeight: 800, letterSpacing: '0.04em', marginBottom: 6 }}>HIS WEBSITE</div>
      {sites.map((s) => (
        <button
          key={s.slug}
          type="button"
          onClick={() => window.open(s.url, '_blank', 'noopener,noreferrer')}
          style={{
            width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
            background: ink.bgDeep, border: `2px solid ${ink.border}`, borderRadius: 12,
            padding: '10px 12px', marginBottom: 6, cursor: 'pointer', color: ink.text,
          }}
        >
          <span style={{ minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 13.5, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.title}</span>
            <span style={{ display: 'block', color: ink.dim, fontSize: 11.5 }}>
              {KIND_WORDS[s.kind] || KIND_WORDS.page}{s.updatedAt ? `, updated ${agoShort(s.updatedAt) === 'now' ? 'just now' : `${agoShort(s.updatedAt)} ago`}` : ''}
            </span>
          </span>
          <span style={{ flexShrink: 0, background: ink.line, color: ink.text, borderRadius: 999, padding: '4px 11px', fontSize: 11.5, fontWeight: 800 }}>go</span>
        </button>
      ))}
    </div>
  )
}

// Grey bones in the dark palette. The rows are the exact height of a real
// action row (two-line: 58px, one-line: 44px), so the hand fades in over
// its own shape and nothing shoves.
function DarkRows({ rows = 6 }) {
  return (
    <div className="flex flex-col" style={{ gap: 8 }}>
      <style>{`@keyframes act-pulse { 0%, 100% { opacity: .35 } 50% { opacity: .75 } }`}</style>
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          style={{
            background: ink.bgDeep, border: `2px solid ${ink.border}`,
            borderRadius: 14, padding: '10px 12px', height: i % 3 === 2 ? 44 : 58,
          }}
        >
          <div style={{ height: 12, width: `${52 + ((i * 17) % 34)}%`, background: ink.stitch, borderRadius: 7, animation: `act-pulse 1.3s ease ${i * 0.14}s infinite` }} />
          {i % 3 !== 2 && (
            <div style={{ height: 10, width: `${28 + ((i * 11) % 22)}%`, background: ink.stitch, borderRadius: 7, marginTop: 8, animation: `act-pulse 1.3s ease ${i * 0.14 + 0.07}s infinite` }} />
          )}
        </div>
      ))}
    </div>
  )
}

function ActionList({ hand, done, busy, signedIn, onDo, onMail, unread, budget }) {
  if (!hand) return <DarkRows rows={7} />
  if (hand.error) return <Note text={hand.error} />
  const spent = budget && budget.left <= 0
  return (
    <div className="flex flex-col" style={{ gap: 8 }}>
      {(hand.persistent || []).map((p) => {
        // Shown when locked rather than hidden: this is the surface people
        // should be able to see themselves climbing toward, and a door
        // that simply is not there teaches nobody it exists. Same shape
        // the locked pool teasers already use.
        const shut = p.locked || !signedIn
        return (
          <button
            key={p.id}
            type="button"
            onClick={shut ? undefined : onMail}
            style={{
              textAlign: 'left',
              background: shut ? 'transparent' : ink.bgDeep,
              border: `2px solid ${shut ? ink.stitch : ink.soft}`,
              borderRadius: 14, padding: '10px 12px',
              cursor: shut ? 'default' : 'pointer',
              opacity: shut ? 0.75 : 1,
            }}
          >
            <div style={{ color: shut ? ink.dim : ink.text, fontSize: 14, fontWeight: 800 }}>
              {p.label}
              {!shut && unread > 0 && <span style={{ marginLeft: 8, color: ink.soft, fontSize: 12 }}>({unread} unread)</span>}
            </div>
            <div style={{ color: ink.dim, fontSize: 12 }}>
              {!signedIn
                ? 'connect first, then your inbox lives here'
                : p.locked
                  ? `he writes to people he knows by sight. keep leaving him things (${p.rung}).`
                  : p.blurb}
            </div>
          </button>
        )
      })}

      {(hand.actions || []).map((a) => {
        const state = done[a.id]
        const failed = state && state !== 'ok'
        const disabled = a.locked || !!state || busy === a.id || (spent && !state)
        return (
          <button
            key={a.id}
            type="button"
            onClick={() => onDo(a)}
            disabled={disabled}
            style={{
              textAlign: 'left',
              background: a.locked ? 'transparent' : ink.bgDeep,
              border: `2px solid ${a.locked ? ink.stitch : ink.border}`,
              borderRadius: 14,
              padding: '10px 12px',
              opacity: a.locked || (spent && !state) ? 0.5 : 1,
              cursor: disabled ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
            }}
          >
            <div>
              <div style={{ color: ink.text, fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                {a.flavour === 'mischief' && <span style={{ width: 7, height: 7, borderRadius: 99, background: ink.danger, flexShrink: 0 }} />}
                {a.flavour === 'fun' && <span style={{ width: 7, height: 7, borderRadius: 99, background: ink.soft, flexShrink: 0 }} />}
                {a.label}
              </div>
              {a.locked && <div style={{ color: ink.dim, fontSize: 12 }}>for {a.rung}</div>}
              {failed && <div style={{ color: ink.danger, fontSize: 12, marginTop: 3 }}>{state}</div>}
            </div>
            {state === 'ok' && <TickCircle />}
          </button>
        )
      })}
    </div>
  )
}


// ── Your actions: the receipts ───────────────────────────────────────────
// Every deed you have done, and what became of it: still waiting where you
// left it, found (with the line that went into his day), or the board
// cycled before he passed. The acknowledgment is the product here; this
// tab is where it lands.
function YoursList({ mine, signedIn, onSeeDiary }) {
  if (!signedIn) return <Note text="connect first: the town keeps receipts by name." />
  if (!mine) return <DarkRows rows={5} />
  if (mine.error) return <Note text={mine.error} />
  if (!mine.length) return <Note text="no deeds yet. do a thing and it lands here." />
  return (
    <div className="flex flex-col" style={{ gap: 8 }}>
      {mine.map((m) => (
        <div
          key={m.id}
          style={{
            background: ink.bgDeep, border: `2px solid ${ink.border}`,
            borderRadius: 14, padding: '10px 12px',
          }}
        >
          <div className="flex items-start justify-between" style={{ gap: 10 }}>
            <div>
              <div style={{ color: ink.text, fontSize: 13.5, fontWeight: 700 }}>{m.label}</div>
              <div style={{ color: ink.dim, fontSize: 11.5, marginTop: 1 }}>
                {m.placeTitle} · {new Date(m.at).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
            {m.status === 'noticed' && <TickCircle />}
          </div>
          {m.status === 'noticed' && (
            <div style={{ marginTop: 7, borderTop: `1.5px solid ${ink.stitch}`, paddingTop: 7 }}>
              <div style={{ color: ink.good, fontSize: 11.5, fontWeight: 800 }}>
                he found it · {new Date(m.noticedAt).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </div>
              {m.trace && (
                <div style={{ color: ink.soft, fontSize: 12.5, fontStyle: 'italic', lineHeight: 1.5, marginTop: 3 }}>
                  "{m.trace}" went into his day
                </div>
              )}
              <button
                type="button"
                onClick={() => onSeeDiary(m)}
                style={{
                  marginTop: 7, background: ink.bg, border: `2px solid ${ink.border}`,
                  borderRadius: 999, color: ink.soft, fontSize: 11.5, fontWeight: 800,
                  padding: '4px 12px', cursor: 'pointer',
                }}
              >
                see it in his diary
              </button>
            </div>
          )}
          {m.status === 'waiting' && (
            <div style={{ color: ink.dim, fontSize: 11.5, marginTop: 5 }}>still out there, waiting for him to pass</div>
          )}
          {m.status === 'missed' && (
            <div style={{ color: ink.dim, fontSize: 11.5, marginTop: 5 }}>the town moved on before he passed</div>
          )}
        </div>
      ))}
    </div>
  )
}

/**
 * The PMail mark: an envelope whose flap folds into an M.
 *
 * Sam wanted it to read like the thing everybody already knows an inbox
 * looks like, so it borrows the SHAPE LANGUAGE (open envelope, a coloured
 * fold, the name set beside it in mixed case) rather than the mark
 * itself, and it is drawn in the popup's own purple palette so it belongs
 * to his town and not to a search company. Inline SVG: no image request,
 * and it stays crisp at any size.
 */
function PMailMark() {
  return (
    <div className="flex items-center" style={{ gap: 7 }}>
      <svg width="21" height="16" viewBox="0 0 40 30" aria-hidden="true" style={{ display: 'block' }}>
        <rect x="1" y="1" width="38" height="28" rx="4" fill={ink.bgDeep} stroke={ink.border} strokeWidth="2" />
        {/* The flap folded into an M, drawn as two strokes that meet in
            the middle. The first cut drew only the V and left the
            uprights to the fills, which reads as a tick rather than a
            letter once it is bigger than a favicon. Two purples instead
            of four brand colours: the shape is the borrowed idea, the
            palette stays his. */}
        <path d="M4 26V7l16 11" fill="none" stroke={ink.line} strokeWidth="4" strokeLinejoin="round" strokeLinecap="round" />
        <path d="M20 18L36 7v19" fill="none" stroke={ink.soft} strokeWidth="4" strokeLinejoin="round" strokeLinecap="round" />
      </svg>
      <span style={{ color: ink.text, fontSize: 13.5, fontWeight: 700, letterSpacing: '-0.01em' }}>
        PMail
      </span>
    </div>
  )
}

// ── His post, as an email client the size of a postcard ─────────────────
//
// A separate popup on purpose: an inbox is a different piece of furniture
// from a noticeboard, and it earns the costume: title bar, folder line,
// rows from PINO, a compose strip. The joke is that it looks like the
// software at the cafe he is sitting in.

function PinoMailPopup({ open, onClose, onEmptied, frameRef }) {
  const [threads, setThreads] = useState(null)
  const [active, setActive] = useState(null)
  const rootRef = frameRef

  useEffect(() => {
    if (!open) return
    setThreads(null)
    setActive(null)
    authFetch('/api/mail', { cache: 'no-store' })
      .then((r) => r.json())
      .then((d) => { setThreads(d.threads || []); onEmptied() })
      .catch(() => setThreads({ error: 'the post office is shut' }))
  }, [open])          // eslint-disable-line react-hooks/exhaustive-deps



  if (!open) return null
  const list = Array.isArray(threads) ? threads : []

  return (
    <div
      ref={rootRef}
      className={[
        'fixed z-50 pointer-events-auto',
        'top-0 left-0 right-0 bottom-0',
        // The right-hand window of the desk: same band as the actions
        // panel, other side, so both sit open like two apps at the cafe.
        'md:top-16 md:right-6 md:bottom-[12rem] md:left-auto md:w-[360px]',
      ].join(' ')}
    >
      <div
        className="relative h-full flex flex-col overflow-hidden"
        style={{
          background: ink.bg,
          border: `2.5px solid ${ink.border}`,
          borderRadius: 22,
          boxShadow: '0 6px 0 rgba(0, 0, 0, 0.45)',
        }}
      >
        {/* Title bar, complete with buttons that do what they look like. */}
        <div
          className="flex items-center justify-between"
          style={{ background: ink.bgDeep, borderBottom: `2px solid ${ink.border}`, padding: '10px 14px' }}
        >
          <div className="flex items-center" style={{ gap: 8 }}>
            <span style={{ width: 10, height: 10, borderRadius: 99, background: ink.danger, cursor: 'pointer' }} onClick={onClose} />
            <span style={{ width: 10, height: 10, borderRadius: 99, background: '#f5c96b' }} />
            <span style={{ width: 10, height: 10, borderRadius: 99, background: ink.good }} />
          </div>
          <PMailMark />
          <div style={{ width: 44 }} />
        </div>

        <div style={{ color: ink.dim, fontSize: 11.5, padding: '8px 16px 6px', borderBottom: `2px solid ${ink.stitch}` }}>
          {active ? (
            <button type="button" onClick={() => setActive(null)} style={{ color: ink.soft, fontWeight: 800, cursor: 'pointer', background: 'none', border: 'none', fontSize: 11.5, padding: 0 }}>
              &lt; inbox
            </button>
          ) : (
            <>inbox ({list.length}) · sent letters arrive when he is next online</>
          )}
        </div>

        <div className="kawaii-scroll flex-1 min-h-0" style={{ overflowY: 'auto' }}>
          {!threads && (
            <div>
              <style>{`@keyframes act-pulse { 0%, 100% { opacity: .35 } 50% { opacity: .75 } }`}</style>
              {[0, 1, 2].map((i) => (
                <div key={i} style={{ padding: '11px 16px', borderBottom: `1.5px solid ${ink.stitch}` }}>
                  <div style={{ height: 12, width: `${30 + i * 8}%`, background: ink.stitch, borderRadius: 7, animation: `act-pulse 1.3s ease ${i * 0.15}s infinite` }} />
                  <div style={{ height: 10, width: `${60 + i * 9}%`, background: ink.stitch, borderRadius: 7, marginTop: 7, animation: `act-pulse 1.3s ease ${i * 0.15 + 0.07}s infinite` }} />
                </div>
              ))}
            </div>
          )}
          {threads?.error && <Note text={threads.error} />}
          {Array.isArray(threads) && !threads.length && (
            <Note text="no letters yet. he writes to people whose things he keeps: leave him offerings, come back." />
          )}
          {!active && list.map((t) => {
            const last = t.messages[t.messages.length - 1]
            const fromHim = last.sender === 'pino'
            return (
              <button
                key={t.threadId}
                type="button"
                onClick={() => setActive(t.threadId)}
                className="block w-full text-left"
                style={{ padding: '11px 16px', borderBottom: `1.5px solid ${ink.stitch}`, background: 'transparent', cursor: 'pointer' }}
              >
                <div className="flex items-center justify-between">
                  <span style={{ color: ink.text, fontSize: 13.5, fontWeight: t.unread ? 800 : 600 }}>
                    {fromHim ? 'Pino' : 'you'}
                    {t.unread > 0 && <span style={{ marginLeft: 7, width: 7, height: 7, borderRadius: 99, background: ink.line, display: 'inline-block' }} />}
                  </span>
                  <span style={{ color: ink.dim, fontSize: 11 }}>
                    {new Date(last.at).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
                  </span>
                </div>
                <div style={{ color: ink.dim, fontSize: 12.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {last.body}
                </div>
              </button>
            )
          })}
          {active && <ThreadView thread={list.find((t) => t.threadId === active)} onSent={() => {
            authFetch('/api/mail', { cache: 'no-store' }).then((r) => r.json()).then((d) => setThreads(d.threads || []))
          }} />}
        </div>
      </div>
    </div>
  )
}

function ThreadView({ thread, onSent }) {
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  if (!thread) return null

  const send = async () => {
    const body = draft.trim()
    if (!body || sending) return
    setSending(true)
    try {
      const r = await authFetch('/api/mail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ threadId: thread.threadId, body }),
      })
      if (r.ok) { setDraft(''); onSent() }
    } finally {
      setSending(false)
    }
  }

  return (
    <div style={{ padding: '10px 16px 16px' }}>
      {thread.messages.map((m) => (
        <div key={m.id} style={{ marginBottom: 12 }}>
          <div style={{ color: m.sender === 'pino' ? ink.soft : ink.dim, fontSize: 11, fontWeight: 800 }}>
            {m.sender === 'pino' ? 'PINO' : 'YOU'}
            <span style={{ color: ink.dim, fontWeight: 600, marginLeft: 8 }}>
              {new Date(m.at).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          <div
            style={{
              color: ink.text, fontSize: 13.5, lineHeight: 1.55, marginTop: 3,
              background: m.sender === 'pino' ? ink.bgDeep : 'transparent',
              border: m.sender === 'pino' ? `1.5px solid ${ink.stitch}` : 'none',
              borderRadius: 10, padding: m.sender === 'pino' ? '8px 10px' : '0',
            }}
          >
            {m.body}
          </div>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') send() }}
          placeholder="write back"
          maxLength={500}
          style={{
            flex: 1, background: ink.bgDeep, border: `2px solid ${ink.border}`, borderRadius: 10,
            color: ink.text, fontSize: 13, padding: '8px 10px', outline: 'none',
          }}
        />
        <button
          type="button"
          onClick={send}
          disabled={sending || !draft.trim()}
          style={{
            background: ink.line, color: ink.text, border: `2px solid ${ink.border}`, borderRadius: 10,
            fontWeight: 800, fontSize: 13, padding: '0 14px', cursor: 'pointer',
            opacity: sending || !draft.trim() ? 0.6 : 1,
          }}
        >
          send
        </button>
      </div>
      <div style={{ color: ink.dim, fontSize: 11, marginTop: 6 }}>
        he reads this the next time he is at the cafe, and answers if he has something to say
      </div>
    </div>
  )
}

function Note({ text }) {
  return <div style={{ color: ink.dim, fontSize: 13, textAlign: 'center', padding: '18px 12px' }}>{text}</div>
}

export default ActionsHud
